<template>
	<div>
		<div class="font-light">
			Querying
			<a
				href="#"
				class="text-base-content underline"
				style="text-decoration-style: dotted; text-underline-offset: 2px"
				@click.prevent="showEndpointDetails = !showEndpointDetails"
				>{{ orgConfig.endpoint.description }}</a
			>
		</div>
		<div v-if="showEndpointDetails" class="p-5 bg-base-200 shadow-inner">
			<span class="font-light">Organization:</span>
			{{ orgConfig?.orgName }}
			<div class="font-light mt-3">DICOMweb endpoint</div>
			<div class="ml-5">
				<span class="font-light">Name:</span> {{ orgConfig.endpoint.name }}
				<br />
				<span class="font-light">Description:</span>
				{{ orgConfig.endpoint.description }}
				<br />
				<span class="font-light">QIDO URL:</span>
				{{ orgConfig.endpoint.qidoBaseUrl }}
				<br />
				<span class="font-light">WADO URL:</span>
				{{ orgConfig.endpoint.wadoBaseUrl }}
			</div>
		</div>
		<FindByAccession
			class="mt-5"
			v-if="orgConfig"
			:url-params="orgConfig.endpoint.params"
			@update="onSelectionUpdate"
		></FindByAccession>
		<button
			class="btn mt-5"
			v-if="selectedSeries.length > 0"
			@click="startLoadingImages"
			:disabled="loadingImages"
		>
			{{ loadingImages ? "Loading..." : "Import images" }}
		</button>
	</div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref } from "vue";
import { OrgConfig, QIDOResult, ResultStudy } from "./types";
import FindByAccession from "./FindByAccession.vue";
import { getClients } from "./dicomweb-client";
import { addLog, progressBar, updateStatus } from "../../logger";
import * as dicomwebClient from "./dicomweb-client";
import type { LoadResult } from "../../loadImages";
import pMap from "p-map";

/**
 * Retrieval is a prop rather than an event because the pipeline below has to
 * await it — `emit` discards whatever the handler returns, which is what let the
 * old `complete` event fire while instances were still being appended.
 */
const props = defineProps<{
	orgConfig: OrgConfig;
	loadSources: (
		sources: { bytes: ArrayBuffer; name: string }[]
	) => Promise<LoadResult>;
}>();

const emit = defineEmits(["complete", "updateOrg"]);

onBeforeMount(() => {
	dicomwebClient.init(
		props.orgConfig.endpoint.wadoBaseUrl!,
		props.orgConfig.endpoint.qidoBaseUrl!
	);
});

const selectedSeries = ref<{ studyUID: string; seriesUID: string }[]>([]);
const showEndpointDetails = ref(false);
const loadingImages = ref(false);

function onSelectionUpdate(result: ResultStudy[]) {
	selectedSeries.value = [];
	result.forEach((study) => {
		if (study.series) {
			study.series.forEach((series) => {
				const seriesUID = series["0020000E"]?.Value?.[0] as string;
				const studyUID = study["0020000D"]?.Value?.[0] as string;
				if (series.selected && seriesUID && studyUID) {
					selectedSeries.value.push({ studyUID, seriesUID });
				}
			});
		}
	});
}

/**
 * Concurrent DICOMweb retrievals. This is the dial that sets wall-clock import
 * time, and it is deliberately independent of how fast we can process what comes
 * back — see `createPipeline`.
 */
const FETCH_CONCURRENCY = 10;

/** Instances handed to the loader per call. */
const PROCESS_BATCH = 2;

/**
 * Ceiling on retrieved-but-not-yet-processed bytes.
 *
 * Fetching is network-bound and processing is main-thread-bound, so they run at
 * unrelated speeds and the queue between them is what absorbs the difference. In
 * practice it stays near-empty; this only matters against a very fast server,
 * where an unbounded queue would let the whole study accumulate on the heap
 * before the loader could spool any of it. Hitting it pauses new retrievals
 * (in-flight ones still finish) until a batch drains.
 */
const MAX_PENDING_BYTES = 256 * 1024 * 1024;

/**
 * The seam between retrieval and processing.
 *
 * Processing is serialized to one batch at a time on purpose. Per instance it is
 * two main-thread parses (dcmjs for the header, dicom-parser for the decode)
 * plus a single frame-0 decode on the worker pool — so overlapping batches adds
 * no parallelism worth having while multiplying peak memory by the overlap. The
 * old code got depth from a fire-and-forget emit, which meant it was whatever
 * the fetch pattern happened to produce.
 */
function createPipeline() {
	const pending: { bytes: ArrayBuffer; name: string }[] = [];
	const waiters: (() => void)[] = [];
	const totals = { loaded: 0, failed: 0 };
	let pendingBytes = 0;
	let processing: Promise<void> = Promise.resolve();
	let draining = false;

	function releaseCapacity(bytes: number) {
		pendingBytes -= bytes;
		while (waiters.length > 0 && pendingBytes < MAX_PENDING_BYTES) {
			waiters.shift()!();
		}
	}

	function schedule() {
		// One drain task at a time; it picks up anything queued while it runs, so
		// re-scheduling would only build a chain of no-ops.
		if (draining) return;
		draining = true;
		processing = processing.then(async () => {
			try {
				while (pending.length > 0) {
					const batch = pending.splice(0, PROCESS_BATCH);
					// Measured before the call: the loader drops its own references to
					// these buffers, but this array still holds them until it returns.
					const batchBytes = batch.reduce(
						(sum, source) => sum + source.bytes.byteLength,
						0
					);
					try {
						const result = await props.loadSources(batch);
						totals.loaded += result.loaded;
						totals.failed += result.failed;
					} catch (err: any) {
						// A batch that throws outright still has to free its capacity and
						// must not break the chain for the batches behind it.
						totals.failed += batch.length;
						addLog(
							"error",
							`Failed to process ${batch.length} retrieved image(s): ${
								err?.message || err
							}`
						);
						console.error(err);
					} finally {
						releaseCapacity(batchBytes);
					}
				}
			} finally {
				// No await between the loop's exit condition and here, so an `add`
				// can't slip in and be left unscheduled.
				draining = false;
			}
		});
	}

	return {
		totals,

		/** Blocks a fetch task when the queue is at its ceiling. */
		async awaitCapacity() {
			while (pendingBytes >= MAX_PENDING_BYTES) {
				await new Promise<void>((resolve) => waiters.push(resolve));
			}
		},

		add(bytes: ArrayBuffer, name: string) {
			pending.push({ bytes, name });
			pendingBytes += bytes.byteLength;
			schedule();
		},

		/** Resolves once everything queued has been processed. */
		async drain() {
			do {
				schedule();
				await processing;
			} while (pending.length > 0);
		},
	};
}

// Load images via DICOMweb
async function startLoadingImages() {
	loadingImages.value = true;

	const pipeline = createPipeline();

	try {
		const { wadoClient, qidoClient } = getClients();

		let series_i = 0;
		for (let { studyUID, seriesUID } of selectedSeries.value) {
			addLog(
				"info",
				`Loading series ${series_i} of ${selectedSeries.value.length}`
			);
			console.log(`Loading series ${series_i} of ${selectedSeries.value.length}`);

			try {
				const result: QIDOResult[] = await qidoClient.searchForInstances({
					studyInstanceUID: studyUID,
					seriesInstanceUID: seriesUID,
				});
				const instanceUIDs = result.map(
					(instance) => instance["00080018"]?.Value?.[0]
				);

				let instance_i = 0;
				progressBar.value.start(instanceUIDs.length);

				await pMap(
					instanceUIDs,
					async (instanceUID) => {
						// Only blocks if processing has fallen far enough behind to fill
						// the queue; normally a no-op.
						await pipeline.awaitCapacity();

						addLog(
							"info",
							`Series ${series_i + 1} of ${
								selectedSeries.value.length
							}: Loading image ${instance_i + 1} of ${instanceUIDs.length}`
						);
						try {
							const instanceArrayBuffer: ArrayBuffer =
								await wadoClient.retrieveInstance({
									studyInstanceUID: studyUID,
									seriesInstanceUID: seriesUID,
									sopInstanceUID: instanceUID,
									...props.orgConfig.endpoint.params,
								});
							console.log(
								`Loaded instance ${instance_i + 1} of ${instanceUIDs.length}`
							);
							// Handed over as raw bytes rather than wrapped in a File: a File
							// built from a buffer is memory-backed, so the registry would
							// have held the whole study on the heap for the session. Passing
							// the buffer lets it spool to OPFS instead.
							pipeline.add(instanceArrayBuffer, instanceUID as string);
						} finally {
							instance_i += 1;
							// Tracks retrieval attempts, which are the long pole — so the
							// bar still reaches the end when some of them fail. Processing
							// continues behind it and is waited on before `complete`.
							progressBar.value.numDone += 1;
						}
					},
					{
						concurrency: FETCH_CONCURRENCY,
						// Let every instance be attempted, and settle them all before
						// this resolves. The default (stop on first error) abandons
						// in-flight tasks, which with the backpressure above could leave
						// one parked in awaitCapacity and have it queue an instance after
						// we'd already declared the import complete. Rejecting the whole
						// series over one bad instance was also the wrong call for a bulk
						// importer — the rest of it is still worth having.
						stopOnError: false,
					}
				);
			} catch (err: any) {
				// With stopOnError: false this is an AggregateError once every task has
				// settled, so nothing is still running behind it.
				const errors: any[] = err?.errors ?? [err];
				for (const e of errors) {
					addLog("error", `Failed to retrieve instance: ${e?.message || e}`);
				}
				console.error(err);
			}

			console.log(`Finished loading series ${series_i + 1}`);
			series_i += 1;
		}

		// Everything is retrieved; wait for the tail of the queue before telling the
		// parent we're done. Without this the stepper advanced and the post-load
		// memory snapshot was taken while instances were still being appended.
		updateStatus("Processing remaining images...");
		await pipeline.drain();

		const { loaded, failed } = pipeline.totals;
		if (loaded === 0) {
			updateStatus("No valid DICOM images were retrieved.");
		} else if (failed === 0) {
			updateStatus(`Loaded ${loaded} image${loaded === 1 ? "" : "s"} successfully.`);
		} else {
			updateStatus(
				`Loaded ${loaded} of ${loaded + failed} images. ${failed} failed — expand logs for details.`
			);
		}
		progressBar.value.reset();

		emit("complete");
	} finally {
		// A retrieval that threw out of the loop above can still have work queued;
		// it has to finish before the parent moves on either way.
		await pipeline.drain();
		loadingImages.value = false;
	}
}
</script>
